import api from './api';

export interface ReviewUserRef {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
}

export interface ReviewReply {
  _id?: string;
  message: string;
  repliedAt: string;
  repliedBy?: ReviewUserRef;
  isEdited?: boolean;
  editedAt?: string;
  helpfulCount?: number;
  unhelpfulCount?: number;
}

export interface SellerReply {
  message?: string;
  repliedAt?: string;
  repliedBy?: ReviewUserRef;
  isEdited?: boolean;
  editedAt?: string;
}

export interface Review {
  _id: string;
  productId?: {
    _id: string;
    name?: string;
    slug?: string;
    images?: string[];
  };
  userId?: ReviewUserRef;
  orderId?: string;
  rating?: number;
  comment: string;
  title?: string;
  isApproved: boolean;
  isVerifiedPurchase: boolean;
  isEdited?: boolean;
  editedAt?: string;
  helpfulCount: number;
  unhelpfulCount: number;
  sellerReply?: SellerReply;
  userReplies?: ReviewReply[];
  moderatedBy?: ReviewUserRef;
  moderatedAt?: string;
  rejectionReason?: string;
  /** Admin currently responsible for moderating this review (assignable plugin). */
  assignedTo?: ReviewUserRef | string | null;
  assignedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewsResponse {
  reviews: Review[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
}

export interface ReviewStats {
  total: number;
  approved: number;
  rejected: number;
  verifiedPurchase: number;
  totalHelpful: number;
  totalUnhelpful: number;
  totalReplies: number;
  withSellerReply: number;
  averageRating: number;
}

export type ReviewStatus = 'approved' | 'pending' | 'rejected';

// ── گزارش تخلف روی دیدگاه ──
export type ReviewReportStatus = 'pending' | 'reviewed' | 'dismissed' | 'actioned';

export interface ReviewReport {
  _id: string;
  reason: string;
  reasonLabel?: string;
  description?: string;
  status: ReviewReportStatus;
  adminNote?: string;
  createdAt: string;
  reviewedAt?: string;
  snapshot?: {
    comment?: string;
    authorName?: string;
    productName?: string;
  };
  reporterId?: { _id: string; firstName?: string; lastName?: string; phoneNumber?: string };
  productId?: { _id: string; name?: string; slug?: string };
  reviewId?: {
    _id: string;
    comment?: string;
    rating?: number;
    isApproved?: boolean;
    reportCount?: number;
    createdAt?: string;
    userId?: { _id: string; firstName?: string; lastName?: string };
  };
}

export interface ReviewReportsResponse {
  reports: ReviewReport[];
  pagination: { total: number; page: number; pages: number; limit: number };
}

export interface ReviewReportStats {
  pending: number;
  reviewed: number;
  dismissed: number;
  actioned: number;
  total: number;
}

class ReviewsService {
  private baseURL = '/admin/reviews';

  /**
   * لیست نظرات با فیلتر و صفحه‌بندی (جستجو سمت سرور روی متن، کاربر و محصول)
   */
  async getReviews(
    page: number = 1,
    limit: number = 20,
    filters?: {
      status?: ReviewStatus | '';
      productId?: string;
      rating?: number;
      search?: string;
      /** '' = default scope, 'me', 'unassigned' — see utils/assignmentScope on the server. */
      assignee?: string;
    }
  ): Promise<ReviewsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (filters?.status) params.append('status', filters.status);
    if (filters?.productId) params.append('productId', filters.productId);
    if (filters?.rating) params.append('rating', filters.rating.toString());
    if (filters?.search?.trim()) params.append('search', filters.search.trim());
    if (filters?.assignee) params.append('assignee', filters.assignee);

    const response: any = await api.get(`${this.baseURL}?${params.toString()}`);
    const data = response?.reviews ? response : response?.data || {};
    return {
      reviews: data.reviews || [],
      pagination: {
        total: data.pagination?.total || 0,
        page: data.pagination?.page || page,
        pages: data.pagination?.pages || 0,
        limit: data.pagination?.limit || limit,
        hasNext: data.pagination?.hasNext,
        hasPrev: data.pagination?.hasPrev
      }
    };
  }

  /**
   * آمار کلی نظرات
   */
  async getStats(): Promise<ReviewStats> {
    const response: any = await api.get(`${this.baseURL}/stats`);
    const stats = response?.stats || response?.data?.stats || response || {};
    return {
      total: stats.total || 0,
      approved: stats.approved || 0,
      rejected: stats.rejected || 0,
      verifiedPurchase: stats.verifiedPurchase || 0,
      totalHelpful: stats.totalHelpful || 0,
      totalUnhelpful: stats.totalUnhelpful || 0,
      totalReplies: stats.totalReplies || 0,
      withSellerReply: stats.withSellerReply || 0,
      averageRating: stats.averageRating || 0
    };
  }

  /**
   * تایید/رد نظر توسط ادمین
   */
  async moderateReview(
    reviewId: string,
    action: 'approve' | 'reject',
    rejectReason?: string
  ): Promise<Review> {
    const response = await api.put(`${this.baseURL}/${reviewId}/moderate`, {
      action,
      isApproved: action === 'approve',
      rejectionReason: action === 'reject' ? rejectReason : undefined
    });
    return (response as any)?.review || (response as any)?.data?.review || (response as any);
  }

  // ── گزارش‌های تخلف روی دیدگاه ──
  async getReports(
    page = 1,
    limit = 20,
    filters?: { status?: ReviewReportStatus | ''; reason?: string }
  ): Promise<ReviewReportsResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.reason) params.append('reason', filters.reason);

    const response: any = await api.get(`/admin/review-reports?${params.toString()}`);
    const data = response?.reports ? response : response?.data || {};
    return {
      reports: data.reports || [],
      pagination: {
        total: data.pagination?.total || 0,
        page: data.pagination?.page || page,
        pages: data.pagination?.pages || 0,
        limit: data.pagination?.limit || limit,
      },
    };
  }

  async getReportStats(): Promise<ReviewReportStats> {
    const response: any = await api.get('/admin/review-reports/stats');
    const s = response?.data ?? response ?? {};
    return {
      pending: s.pending || 0,
      reviewed: s.reviewed || 0,
      dismissed: s.dismissed || 0,
      actioned: s.actioned || 0,
      total: s.total || 0,
    };
  }

  /** رسیدگی: dismiss = گزارش بی‌مورد بود؛ hide = دیدگاه از دید عمومی خارج شود */
  async reviewReport(
    reportId: string,
    action: 'dismiss' | 'hide',
    adminNote?: string
  ): Promise<void> {
    await api.patch(`/admin/review-reports/${reportId}/review`, { action, adminNote });
  }

  /**
   * وضعیت نظر. مدل فیلد status ندارد و وضعیت از دو فیلد ساخته می‌شود:
   * تاییدنشده‌ای که ادمین رویش کار نکرده (moderatedAt ندارد) «در انتظار» است،
   * نه «رد شده» — این تفکیک از وقتی لازم شد که تنظیم
   * «تأیید دیدگاه قبل از نمایش» می‌تواند روشن باشد.
   */
  getStatus(review: Review): ReviewStatus {
    if (review.isApproved) return 'approved';
    return review.moderatedAt ? 'rejected' : 'pending';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labelMap: Record<string, string> = {
      approved: 'تایید شده',
      pending: 'در انتظار تایید',
      rejected: 'رد شده'
    };
    return labelMap[status] || status;
  }
}

export const reviewsService = new ReviewsService();
export default reviewsService;
